import React from "react";
import { useQuery } from "@tanstack/react-query";
import { formatInTimeZone } from "date-fns-tz";
import { Eye, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CohortsKeys, getAllCohorts } from "@/features/cohorts/api/cohorts";

import CohortDetailsDialog from "./CohortDetailsDialog";

interface CohortsTableProps {}

const CohortsTable: React.FC<CohortsTableProps> = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: [CohortsKeys.GET_COHORTS],
    queryFn: getAllCohorts,
  });

  // Ensure data is always an array
  const cohorts = Array.isArray(data) ? data : [];

  const [viewCohortId, setViewCohortId] = React.useState<string | null>(null);

  if (isLoading) {
    return (
      <Card className="p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-8">
        <p className="text-center text-destructive">Failed to load cohorts</p>
      </Card>
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Start Date</TableHead>
            <TableHead>Founders</TableHead>
            <TableHead>Paid Founders</TableHead>
            <TableHead>Created At</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cohorts.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={5}
                className="text-center text-muted-foreground"
              >
                No cohorts found. Create your first cohort to get started.
              </TableCell>
            </TableRow>
          ) : (
            cohorts.map((cohort) => (
              <TableRow key={cohort._id}>
                <TableCell
                  className="cursor-pointer"
                  onClick={() => setViewCohortId(cohort._id)}
                >
                  {formatInTimeZone(cohort.startDate, "America/New_York", "MMMM d, yyyy")}
                </TableCell>
                <TableCell
                  className="cursor-pointer font-medium"
                  onClick={() => setViewCohortId(cohort._id)}
                >
                  {cohort.foundersCount}
                </TableCell>
                <TableCell
                  className="cursor-pointer font-medium"
                  onClick={() => setViewCohortId(cohort._id)}
                >
                  {cohort.paidFoundersCount}
                </TableCell>
                <TableCell
                  className="cursor-pointer"
                  onClick={() => setViewCohortId(cohort._id)}
                >
                  {formatInTimeZone(cohort.createdAt, "UTC", "MMM d, yyyy")}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setViewCohortId(cohort._id)}
                    className="gap-1"
                  >
                    <Eye className="h-3 w-3" />
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <CohortDetailsDialog
        cohortId={viewCohortId}
        onClose={() => setViewCohortId(null)}
      />
    </Card>
  );
};

export default CohortsTable;
